import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useQueryClient } from "@tanstack/react-query";
import { useCreateZostava } from "@/api/hooks/useZostavy";
import { toast } from "@/components/ui/use-toast";
import { api, extractErrorMessage } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type { Celok, Zostava } from "@/api/schemas";
import { skupinyZostav } from "@/lib/celky";
import { pismena, suggestNextZostavaKod } from "@/lib/dielecKod";

// Kód (`A`, `B`…) pridelí appka podľa poradia (KOLO 134) — user ho
// nezadáva; TAN číslo patrí do názvu.
const schema = z.object({
  nazov: z.string().min(1, "Názov je povinný").max(255),
  pocet_ks: z.coerce.number().int().min(1, "Min 1 ks"),
});

type FormData = z.infer<typeof schema>;

export interface ZostavaCreateDialogProps {
  zakazkaId: string;
  variantaId: string;
  existingZostavy: Zostava[];
  /**
   * Nová zostava pôjde hneď pod túto (KOLO 196 — Pridať zostavu z menu
   * zostavy). Bez nej na koniec, ako horné tlačidlo a Ctrl+Shift+Z.
   */
  poZostave?: string | null;
  /**
   * Celok, do ktorého nová pôjde — na jeho koniec (KOLO 235). Pod zostavou
   * (`poZostave`) ide do jej celku.
   */
  doCelku?: string | null;
  celky?: Celok[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Koľko zostáv je pred koncom celku — písmeno novej na jeho konci (KOLO 235). */
function poslednaVCelku(zostavy: Zostava[], celky: Celok[], celokId: string): number {
  let n = 0;
  for (const s of skupinyZostav(zostavy, celky)) {
    n += s.zostavy.length;
    if (s.celok?.id === celokId) break;
  }
  return n;
}

export function ZostavaCreateDialog({
  zakazkaId,
  variantaId,
  existingZostavy,
  poZostave = null,
  doCelku = null,
  celky = [],
  open,
  onOpenChange,
}: ZostavaCreateDialogProps): JSX.Element {
  const createMutation = useCreateZostava(zakazkaId, variantaId);
  const qc = useQueryClient();
  const nad = existingZostavy.find((z) => z.id === poZostave) ?? null;
  const nadKod = nad?.kod ?? null;
  // Pod zostavou do jej celku, inak do zadaného (KOLO 235)
  const celokId = nad ? (nad.celok_id ?? null) : doCelku;
  const celok = celky.find((c) => c.id === celokId) ?? null;
  // Kód, ktorý nová dostane — pod zostavou písmeno za ňou (ďalšie sa posunú)
  const kodNovej =
    nadKod !== null
      ? pismena(existingZostavy.findIndex((z) => z.id === poZostave) + 1)
      : celok
        ? pismena(poslednaVCelku(existingZostavy, celky, celok.id))
        : suggestNextZostavaKod(existingZostavy);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { nazov: "", pocet_ks: 1 },
  });

  useEffect(() => {
    if (!open) {
      reset({ nazov: "", pocet_ks: 1 });
    }
  }, [open, reset]);

  async function onSubmit(values: FormData): Promise<void> {
    try {
      const kod = suggestNextZostavaKod(existingZostavy);
      const nova = await createMutation.mutateAsync({
        kod,
        nazov: values.nazov,
        pocet_ks: values.pocet_ks,
        poradie: existingZostavy.length,
        celok_id: celok?.id ?? null,
      });
      if (nadKod !== null) {
        // Pod zostavu z menu — preradí sa hneď po vytvorení. Krok späť
        // ostáva jeden (zmazanie novej): poradie ostatných sa ním vráti
        // a kódy dorovná server (KOLO 134)
        const ids = existingZostavy.map((z) => z.id);
        ids.splice(ids.indexOf(poZostave as string) + 1, 0, nova.data.id);
        await api.post(`/varianty/${variantaId}/zostavy/poradie`, { zostavy: ids });
        await qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      }
      toast({
        title: "Zostava pridaná",
        description: `${nadKod !== null ? kodNovej : nova.data.kod} · ${values.nazov}`,
      });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {nadKod !== null
              ? `Pridať zostavu pod ${nadKod}`
              : celok
                ? `Pridať zostavu do celku ${celok.nazov}`
                : "Pridať zostavu"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Nová zostava v tomto výpočte — názov a počet kusov; kód pridelí
            appka.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="zostava-nazov">
              Názov *{" "}
              <span className="text-xs font-normal text-muted-foreground">
                (TAN číslo patrí sem — kód {kodNovej} dá appka)
              </span>
            </Label>
            <Input
              id="zostava-nazov"
              autoFocus
              autoComplete="off"
              placeholder="TAN6162 pult kasa"
              {...register("nazov")}
            />
            {errors.nazov ? (
              <p className="text-xs text-destructive">{errors.nazov.message}</p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="zostava-pocet">Počet kusov</Label>
            <Input
              id="zostava-pocet"
              type="number"
              min={1}
              {...register("pocet_ks")}
            />
            {errors.pocet_ks ? (
              <p className="text-xs text-destructive">
                {errors.pocet_ks.message}
              </p>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Zrušiť
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Vytvára sa…" : "Pridať"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
